/**
 * The door between the two halves of the game.
 *
 * A campaign is played in two places — the realm, where hosts march and
 * provinces change hands, and your capital, where the actual town is. This is
 * the control that moves the player between them, and the reason it is its own
 * file rather than another panel in the HUD is that it has to be visible in
 * *both* places and outlive the world being swapped underneath it.
 *
 * It also carries the one piece of strategic information the city genuinely
 * needs: whether anything is marching at you, and how long you have. A player
 * laying out a new quarter should be able to see, without leaving, that there
 * are eleven hundred Ironfang three provinces out and closing.
 *
 * Styles are injected under their own id rather than added to `style.ts`, so
 * this panel can be added, moved or deleted without touching the stylesheet
 * everything else shares.
 */

import { setHtml } from './dom.ts';
import { toCss, C } from '../data/palette.ts';
import { nationAccent, nationDef } from '../data/nations.ts';
import { provinceDef } from '../data/provinces.ts';
import type { World } from '../sim/world.ts';
import { armyPoints } from '../sim/campaign/armies.ts';
import { neighbours } from '../sim/campaign/graph.ts';
import { settledHoldings } from '../sim/campaign/holdworld.ts';
import { formatDateShort } from '../sim/systems/calendar.ts';

export interface TravelCallbacks {
  /** Leave the city and open the realm. */
  onToRealm(): void;
  /**
   * Go and stand in another town of yours, by province index.
   *
   * *"you can swap between towns once you capture to build both"*. The swap
   * lives here rather than on the world map because this is the panel that is
   * on screen in **both** halves of the game and outlives the world being
   * swapped underneath it — which is the reason this file exists at all, and it
   * is the same reason twice.
   */
  onEnterHold(province: number): void;
}

/** A host that is close enough to the capital to be worth worrying about. */
interface Threat {
  nation: number;
  points: number;
  /** Provinces away: 0 = it is here, 1 = next door. */
  distance: number;
  turns: number;
}

export class Travel {
  private el: HTMLElement;
  private visible = false;
  /**
   * Whether the list of the crown's towns is down.
   *
   * Held here and written into the markup as a class, so `setHtml`'s "only
   * write when the words changed" comparison sees the open and closed panels as
   * two different panels and redraws once on each transition. A flag kept out
   * of the markup would be a flag the panel never noticed.
   */
  private listOpen = false;

  constructor(private cb: TravelCallbacks) {
    injectTravelStyle();
    this.el = document.createElement('div');
    this.el.id = 'travel';
    this.el.style.display = 'none';
    (document.getElementById('ui-root') as HTMLElement).appendChild(this.el);
  }

  show(): void {
    this.visible = true;
    this.el.style.display = '';
  }

  hide(): void {
    this.visible = false;
    this.el.style.display = 'none';
  }

  get isOpen(): boolean {
    return this.visible;
  }

  /**
   * Redraw. Called every frame with whichever world is on screen; a world with
   * no campaign attached (a skirmish, a standalone battle) hides the panel, so
   * the door only exists where it leads somewhere.
   */
  update(city: World, campaign: World | null): void {
    if (!this.visible) return;
    const c = campaign?.campaign;
    if (!c) {
      setHtml(this.el, '');
      return;
    }

    /**
     * Where the player is standing, which is no longer the same question as
     * "where is your capital".
     *
     * This line read `c.capital` and printed the seat's name on the panel
     * whatever town you were in — so a player standing in a captured hamlet was
     * told, in the one place on screen that says where you are, that they were
     * at home. `config.province` is written by `holdConfigFor` and absent on
     * every world made before a crown could hold two, which is exactly the set
     * of worlds that *are* the capital.
     */
    const here = city.config.province ?? c.capital;
    const prov = provinceDef(here);
    const nation = nationDef(c.nations[c.playerNation].def);
    const accent = toCss(nationAccent(c.nations[c.playerNation].def));

    const threats = threatsToCapital(campaign as World);
    const worst = threats[0];

    const threatHtml = worst
      ? `<div class="tv-threat ${worst.distance === 0 ? 'here' : ''}">
           <b>${esc(nationDef(c.nations[worst.nation].def).name)}</b>
           ${worst.distance === 0
             ? 'is at the gates.'
             : `is ${worst.distance === 1 ? 'next door' : `${worst.distance} provinces out`} — ` +
               `about ${worst.turns} month${worst.turns === 1 ? '' : 's'}.`}
           <span class="tv-pts">${Math.round(worst.points)} pts</span>
         </div>`
      : `<div class="tv-threat quiet">Nothing on the roads. For now.</div>`;

    /**
     * Through `setHtml`, because the last element in here is a *button*.
     *
     * This ran every frame and replaced the whole bar every frame, which meant
     * "To the Realm" was a fresh element by the time any human finished pressing
     * it — and a `click` needs the press and the release to land on the same
     * node. The one panel whose whole job is to be the door between the two
     * halves of the game was a door that only opened for the keyboard. The
     * contents change once a simulated day, when the date does.
     */
    /**
     * The crown's other towns, and the list that swaps between them.
     *
     * Every holding the player has stood in is a real settlement world that has
     * been running the whole time; this is how you get back to one. Only drawn
     * when there is more than one — a player with a single town does not need a
     * menu offering it to them, and an affordance that never does anything is
     * worse than no affordance.
     *
     * The seat is marked rather than sorted to the top: the list is in province
     * order, which is the order the world map is in, so a player who knows
     * where their towns are on the chart can find them here.
     */
    const held = settledHoldings(c);
    const many = held.length > 1;
    const listHtml =
      many && this.listOpen
        ? `<div class="tv-list">${held
            .map((q) => {
              const wrecked = c.provinces[q].wreck > 0.005;
              return (
                `<div class="tv-town${q === here ? ' on' : ''}" data-prov="${q}">` +
                `<span class="tv-tname">${esc(provinceDef(q).name)}</span>` +
                `<span class="tv-tnote">${
                  q === c.capital ? 'the seat' : wrecked ? 'wrecked' : rankNote(c, q)
                }</span></div>`
              );
            })
            .join('')}</div>`
        : '';

    setHtml(
      this.el,
      `<div class="tv-where${many ? ' pick' : ''}${this.listOpen ? ' open' : ''}" id="tv-where">
        <span class="tv-dot" style="background:${accent}"></span>
        <div>
          <div class="tv-city">${esc(prov.name)}${many ? '<span class="tv-caret">▾</span>' : ''}</div>
          <div class="tv-nation">${esc(nation.name)} · ${esc(formatDateShort(campaign))}${
            many ? ` · ${held.length} towns` : ''
          }</div>
        </div>
        ${listHtml}
      </div>
      ${threatHtml}
      <div class="tv-go" id="tv-go">To the Realm <span class="tv-key">Tab</span></div>`,
    );

    const go = this.el.querySelector('#tv-go') as HTMLElement | null;
    if (go) go.onclick = () => this.cb.onToRealm();
    const where = this.el.querySelector('#tv-where') as HTMLElement | null;
    if (where && many) {
      where.onclick = (ev) => {
        const row = (ev.target as HTMLElement | null)?.closest('.tv-town') as HTMLElement | null;
        if (row) {
          this.listOpen = false;
          const q = Number(row.dataset.prov);
          if (q !== here) this.cb.onEnterHold(q);
          return;
        }
        this.listOpen = !this.listOpen;
      };
    }
  }

  /** Close the town list — for a world swap, which invalidates every row in it. */
  closeList(): void {
    this.listOpen = false;
  }
}

/** A one-word reading of what this holding is, for the swap list. */
function rankNote(c: NonNullable<World['campaign']>, p: number): string {
  const t = c.provinces[p].tier;
  if (t >= 8) return 'a great city';
  if (t >= 6) return 'a city';
  if (t >= 4) return 'a town';
  return 'a village';
}

/**
 * Everything hostile within a few provinces of the capital, worst first.
 *
 * A breadth-first walk rather than a straight-line distance, because the roads
 * are what an army actually has to march down — a host two provinces away
 * across a river is further away than the map looks.
 */
export function threatsToCapital(campaign: World, maxDepth = 3): Threat[] {
  const c = campaign.campaign;
  if (!c) return [];
  const capital = c.capital;

  const depth = new Map<number, number>([[capital, 0]]);
  const queue = [capital];
  for (let head = 0; head < queue.length; head++) {
    const p = queue[head];
    const d = depth.get(p) ?? 0;
    if (d >= maxDepth) continue;
    for (const n of neighbours(c, p)) {
      if (depth.has(n)) continue;
      depth.set(n, d + 1);
      queue.push(n);
    }
  }

  const out: Threat[] = [];
  for (const army of c.armies) {
    if (army.owner === c.playerNation) continue;
    // Where it will be, not where it is: an army mid-march is already committed.
    const at = army.moveTo >= 0 ? army.moveTo : army.at;
    const d = depth.get(at);
    if (d === undefined) continue;
    if (!(c.nations[army.owner]?.atWar[c.playerNation] === 1)) continue;
    out.push({
      nation: army.owner,
      points: armyPoints(army),
      distance: d,
      // One turn per province of road, plus whatever is left of its current leg.
      turns: Math.max(1, d + (army.moveTo >= 0 ? army.moveT : 0)),
    });
  }
  // Closest first, then heaviest — the thing arriving soonest is the thing that
  // decides what you build this turn.
  out.sort((a, b) => a.distance - b.distance || b.points - a.points);
  return out;
}

function esc(s: string): string {
  return s.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch] as string);
}

let styled = false;
function injectTravelStyle(): void {
  if (styled) return;
  styled = true;
  const s = document.createElement('style');
  s.id = 'travel-style';
  s.textContent = `
    /**
     * Below the resource strip, and inset past the Works panel on the left and
     * the Companies panel on the right. Sat at the very top first, where it
     * covered the resource strip and half the scouting report — the HUD owns
     * that band and this is a guest in it.
     */
    #travel { position: fixed; top: 46px; right: 288px;
      z-index: 40; display: flex; align-items: stretch; gap: 0;
      font-family: Inter, system-ui, sans-serif; pointer-events: none; }
    #travel > * { pointer-events: auto; }
    #travel .tv-where, #travel .tv-threat, #travel .tv-go {
      background: rgba(16,15,13,0.9); border: 1px solid rgba(217,207,188,0.16);
      padding: 8px 14px; display: flex; align-items: center; gap: 10px; }
    #travel .tv-where { border-radius: 3px 0 0 3px; border-right: none; }
    #travel .tv-threat { border-right: none; font-size: 12px; color: ${toCss(C.boneDim)}; max-width: 340px; }
    #travel .tv-threat b { color: ${toCss(C.bone)}; font-weight: 500; }
    #travel .tv-threat.here { color: ${toCss(C.oxbloodLight)}; }
    #travel .tv-threat.here b { color: ${toCss(C.oxbloodLight)}; }
    #travel .tv-threat.quiet { color: ${toCss(C.boneDeep)}; font-style: italic; }
    #travel .tv-pts { color: ${toCss(C.boneDeep)}; font-variant-numeric: tabular-nums; margin-left: 4px; }
    #travel .tv-dot { width: 9px; height: 9px; border-radius: 50%; flex: none;
      box-shadow: 0 0 0 1px rgba(0,0,0,0.6); }
    #travel .tv-city { font-family: Cinzel, Georgia, serif; font-size: 14px;
      letter-spacing: 0.1em; color: ${toCss(C.bone)}; }
    #travel .tv-nation { font-size: 10.5px; letter-spacing: 0.16em; text-transform: uppercase;
      color: ${toCss(C.boneDeep)}; margin-top: 2px; }
    /* The town switcher. The name block is the positioning anchor, not the bar:
       the bar is a flex row, and a list hung off it would stretch the whole
       width of it. (No backticks in here — this block is a template literal.) */
    #travel .tv-where { position: relative; }
    #travel .tv-where.pick { cursor: pointer; }
    #travel .tv-where.pick:hover .tv-city { color: ${toCss(C.goldPale)}; }
    #travel .tv-caret { font-size: 9px; margin-left: 6px; vertical-align: 2px;
      color: ${toCss(C.boneDeep)}; }
    #travel .tv-where.open .tv-caret { color: ${toCss(C.gold)}; }
    #travel .tv-list { position: absolute; top: 100%; left: -1px; min-width: calc(100% + 2px);
      margin-top: 2px; background: rgba(16,15,13,0.97);
      border: 1px solid rgba(217,207,188,0.22); border-radius: 3px;
      box-shadow: 0 8px 22px rgba(0,0,0,0.6); z-index: 2; overflow: hidden;
      max-height: 320px; overflow-y: auto; }
    #travel .tv-town { display: flex; align-items: baseline; justify-content: space-between;
      gap: 16px; padding: 7px 12px; white-space: nowrap;
      border-bottom: 1px solid rgba(217,207,188,0.07); }
    #travel .tv-town:last-child { border-bottom: none; }
    #travel .tv-town:hover { background: rgba(200,164,76,0.16); }
    #travel .tv-town.on { background: rgba(200,164,76,0.10); }
    #travel .tv-tname { font-family: Cinzel, Georgia, serif; font-size: 12.5px;
      letter-spacing: 0.06em; color: ${toCss(C.bone)}; }
    #travel .tv-town.on .tv-tname { color: ${toCss(C.goldPale)}; }
    #travel .tv-tnote { font-size: 10px; letter-spacing: 0.14em; text-transform: uppercase;
      color: ${toCss(C.boneDeep)}; }
    #travel .tv-go { border-radius: 0 3px 3px 0; cursor: pointer;
      font-size: 11.5px; letter-spacing: 0.18em; text-transform: uppercase;
      color: ${toCss(C.gold)}; transition: background 120ms, color 120ms; }
    #travel .tv-go:hover { background: rgba(200,164,76,0.16); color: ${toCss(C.goldPale)}; }
    #travel .tv-key { font-size: 9.5px; letter-spacing: 0.1em; color: ${toCss(C.boneDeep)};
      border: 1px solid rgba(217,207,188,0.22); border-radius: 2px; padding: 1px 5px; }

    /* Narrow screens. This bar is pinned 288px from the right edge because that
       is where the roster column ends — and on a phone there is no roster
       column, so it hung off the left of the screen as a three-word stack. It
       becomes one strip across the top of the map instead, with the threat
       reading dropped: it is a sentence, and a sentence does not fit. */
    @media (max-width: 820px) {
      #travel { top: 48px; right: 4px; left: 4px; }
      #travel .tv-threat { display: none; }
      #travel .tv-where { flex: 1; min-width: 0; }
      #travel .tv-where, #travel .tv-go { padding: 7px 10px; }
      #travel .tv-city { font-size: 12px; white-space: nowrap; overflow: hidden;
        text-overflow: ellipsis; }
      #travel .tv-nation { display: none; }
      #travel .tv-key { display: none; }
    }
  `;
  document.head.appendChild(s);
}
